import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Plus, FolderOpen, TrendingUp, Calendar, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useWorkspaceStore } from '@/store/workspace';
import { getWorkspaceProjects, getWorkspaceOverview } from '@/lib/database';
import { formatDistanceToNow } from 'date-fns';
import type { Tables } from '@/types/database.types';

type ProjectWithStats = Tables<'projects'> & {
  progress: number;
  nextMilestone: string | null;
  nextMilestoneDue: string | null;
  totalMilestones: number;
  completedMilestones: number;
  teamMembers: number;
};

interface WorkspaceOverview {
  total_projects: number;
  on_track_projects: number;
  at_risk_projects: number;
  off_track_projects: number;
  total_users: number;
  admin_users: number;
}

interface DashboardProps {
  isDemo?: boolean;
}

export function Dashboard({ isDemo = false }: DashboardProps) {
  const navigate = useNavigate();
  const { currentWorkspace, canEdit } = useWorkspaceStore();
  const [projects, setProjects] = useState<ProjectWithStats[]>([]);
  const [overview, setOverview] = useState<WorkspaceOverview | null>(null);
  const [loading, setLoading] = useState(true);

  // Load real data when not in demo mode
  useEffect(() => {
    const loadData = async () => {
      if (!isDemo && currentWorkspace) {
        setLoading(true);
        try {
          const [projectsData, overviewData] = await Promise.all([
            getWorkspaceProjects(currentWorkspace.id),
            getWorkspaceOverview(currentWorkspace.id)
          ]);
          setProjects(projectsData);
          setOverview(overviewData);
        } catch (error) {
          console.error('Failed to load dashboard data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
  }, [isDemo, currentWorkspace]);

  // Mock data for demonstration
  const mockProjects = [
    {
      id: '1',
      name: 'Mobile App Redesign',
      status: 'on_track',
      lastUpdate: '2 days ago',
      progress: 75,
    },
    {
      id: '2',
      name: 'API Integration',
      status: 'at_risk',
      lastUpdate: '5 days ago',
      progress: 45,
    },
    {
      id: '3',
      name: 'User Analytics Dashboard',
      status: 'off_track',
      lastUpdate: '1 week ago',
      progress: 20,
    },
  ];

  // Use appropriate data based on demo mode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const displayProjects: any[] = isDemo ? mockProjects : projects;
  const displayStats = isDemo ? {
    total_projects: mockProjects.length,
    on_track_projects: mockProjects.filter(p => p.status === 'on_track').length,
    at_risk_projects: mockProjects.filter(p => p.status === 'at_risk').length,
    off_track_projects: mockProjects.filter(p => p.status === 'off_track').length,
    total_users: 5,
    admin_users: 2
  } : overview;

  // Show loading spinner
  if (!isDemo && loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Show empty state if no projects
  if (!isDemo && displayProjects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="border-2 border-dashed border-gray-300 bg-gray-50/50 max-w-2xl w-full">
          <CardContent className="flex flex-col items-center justify-center py-16 px-8">
            <div className="rounded-full bg-gray-100 p-4 mb-6">
              <FolderOpen className="h-12 w-12 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center max-w-md">
              You won't get very far in life until you have a project
            </h2>
            <p className="text-gray-600 mb-8 max-w-md text-center">
              Projects help you organize your work, track progress, and achieve your goals.
              Create your first project to get started on your journey.
            </p>
            {canEdit() && (
              <Button
                onClick={() => navigate('/projects/create')}
                size="lg"
                className="gap-2"
              >
                <Plus className="h-5 w-5" />
                Create Your First Project
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'green': return 'text-green-600 bg-green-50';
      case 'yellow': return 'text-yellow-600 bg-yellow-50';
      case 'red': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getHealthLabel = (health: string) => {
    switch (health) {
      case 'green': return 'On Track';
      case 'yellow': return 'At Risk';
      case 'red': return 'Off Track';
      default: return 'Unknown';
    }
  };

  const formatLastUpdated = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  // Sort projects by updated_at for Recent Projects
  const recentProjects = isDemo
    ? displayProjects
    : [...displayProjects].sort((a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      ).slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome back!
          </h1>
          <p className="text-gray-600 mt-1">
            Here's what's happening with your projects in {currentWorkspace?.name}
          </p>
        </div>
        {canEdit() && (
          <Button
            onClick={() => navigate('/projects/create')}
            className="mt-4 sm:mt-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayStats?.total_projects || 0}</div>
            <p className="text-xs text-muted-foreground">
              {isDemo ? '+1 from last month' : 'Total projects'}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">On Track</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {isDemo ? mockProjects.filter(p => p.status === 'on_track').length : displayStats?.on_track_projects || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Projects meeting goals
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">At Risk</CardTitle>
            <Calendar className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {isDemo ? mockProjects.filter(p => p.status === 'at_risk').length : displayStats?.at_risk_projects || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Need attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Off Track</CardTitle>
            <Calendar className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {isDemo ? mockProjects.filter(p => p.status === 'off_track').length : displayStats?.off_track_projects || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Require immediate action
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Projects</CardTitle>
              <CardDescription>
                Your latest project activity and status updates
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/projects')}
            >
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentProjects.map((project) => (
              <div
                key={project.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="flex-1">
                  <h3 className="font-medium">{project.name}</h3>
                  <p className="text-sm text-gray-600">
                    Last updated: {isDemo ? project.lastUpdate : formatLastUpdated(project.updated_at)}
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm font-medium">{isDemo ? project.progress : (project.completion_percentage || 0)}%</div>
                    <div className="w-20 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-ingesta-teal-500 h-2 rounded-full"
                        style={{ width: `${isDemo ? project.progress : (project.completion_percentage || 0)}%` }}
                      />
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getHealthColor(isDemo ? project.status : (project.overall_health || 'green'))}`}>
                    {getHealthLabel(isDemo ? project.status : (project.overall_health || 'green'))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}